import { FACTOR_COLUMNS, KPI_COLUMNS, SHEET } from "./excelSpec";

/**
 * A cell as plain text. Formulas give their result, rich text its runs, and a
 * number keeps full precision (Excel's display formatting is not applied).
 * @param {*} value exceljs cell value
 */
export const cellText = (value) => {
  if (value == null) return "";
  if (typeof value === "object") {
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    if ("result" in value) return cellText(value.result);
    if (Array.isArray(value.richText)) return value.richText.map((run) => run.text).join("").trim();
    if ("text" in value) return cellText(value.text);
    return "";
  }
  return String(value).trim();
};

const norm = (text) => String(text ?? "").trim().toLowerCase();

/**
 * One sheet's rows as `{ line, cells }`, columns found by header text so a
 * reordered or trimmed sheet still reads. A column that is missing is absent
 * from `present`, which the planner reads as "leave this field alone".
 *
 * @returns {{ lines: { line: number, cells: Record<string, string> }[], present: Set<string>, missing: boolean }}
 */
const readSheet = (sheet, columns) => {
  if (!sheet) return { lines: [], present: new Set(), missing: true };

  const byHeader = new Map(columns.map((c) => [norm(c.header), c.key]));
  const keyAt = {};
  sheet.getRow(1).eachCell((cell, col) => {
    const key = byHeader.get(norm(cellText(cell.value)));
    if (key) keyAt[col] = key;
  });

  const lines = [];
  sheet.eachRow((row, line) => {
    if (line === 1) return;
    const cells = {};
    let any = false;
    Object.keys(keyAt).forEach((col) => {
      const text = cellText(row.getCell(Number(col)).value);
      cells[keyAt[col]] = text;
      if (text) any = true;
    });
    if (any) lines.push({ line, cells });
  });

  return { lines, present: new Set(Object.values(keyAt)), missing: false };
};

/**
 * The two data sheets of an uploaded KPI workbook, as text. Nothing is
 * interpreted here — planImport decides what each cell means.
 *
 * @param {import("exceljs").Workbook} workbook
 */
export const readWorkbook = (workbook) => {
  const kpis = readSheet(workbook.getWorksheet(SHEET.kpis), KPI_COLUMNS);
  const factors = readSheet(workbook.getWorksheet(SHEET.factors), FACTOR_COLUMNS);

  const problems = [];
  if (kpis.missing && factors.missing) {
    problems.push(`This is not a KPI workbook: it has no "${SHEET.kpis}" or "${SHEET.factors}" sheet`);
  }
  if (!kpis.missing && kpis.lines.length && !kpis.present.has("title") && !kpis.present.has("id")) {
    problems.push(`The "${SHEET.kpis}" sheet needs a "KPI ID" or "KPI title" column`);
  }

  return { kpis, factors, problems };
};
